/**
 * Part 8 — Enterprise Trust admin section.
 *
 * Five tabs:
 *   - Auth Policy (MFA / session / password / IP allowlist)
 *   - SSO (OIDC + SAML configs)
 *   - SCIM Tokens
 *   - Audit Log (with chain verify + export)
 *   - Tenant Data (export + deletion request)
 *
 * Compact UI — single-screen surfaces; relies on theme tokens / Tailwind.
 */
import React, { useEffect, useState } from 'react'
import { toast } from 'sonner'
import {
  enterpriseTrustService,
  zeroTrustService,
  downloadBlob,
  type AuthPolicy,
  type SSOConfig,
  type AuditEntry,
  type SCIMTokenInfo,
  type DeletionStatus,
  type SOC2EvidenceBundle,
} from '../../services/enterpriseTrust'
import type { ApiErrorLike } from '../../utils/apiError'

export type TrustTab = 'policy' | 'sso' | 'scim' | 'audit' | 'tenant' | 'soc2'
type Tab = TrustTab

export function EnterpriseTrustSection({ initialTab = 'policy' }: { initialTab?: TrustTab } = {}) {
  const [tab, setTab] = useState<Tab>(initialTab)
  return (
    <section className="space-y-4">
      <header>
        <h2 className="text-xl font-semibold text-[#102a43]">Enterprise Trust</h2>
        <p className="text-sm text-[#556B7C]">
          Auth policy, SSO, SCIM provisioning, tamper-evident audit log, and
          tenant export / deletion.
        </p>
      </header>
      <nav className="flex gap-2 border-b border-[#E4ECF1]">
        {(['policy', 'sso', 'scim', 'audit', 'tenant', 'soc2'] as Tab[]).map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-3 py-2 text-sm font-medium ${
              tab === t
                ? 'border-b-2 border-[#0967D2] text-[#0967D2]'
                : 'text-[#556B7C]'
            }`}
          >
            {labelOf(t)}
          </button>
        ))}
      </nav>
      {tab === 'policy' && <AuthPolicyPanel />}
      {tab === 'sso' && <SSOPanel />}
      {tab === 'scim' && <SCIMPanel />}
      {tab === 'audit' && <AuditPanel />}
      {tab === 'tenant' && <TenantDataPanel />}
      {tab === 'soc2' && <SOC2EvidencePanel />}
    </section>
  )
}

function labelOf(t: Tab): string {
  return {
    policy: 'Auth Policy',
    sso: 'SSO',
    scim: 'SCIM',
    audit: 'Audit',
    tenant: 'Tenant Data',
    soc2: 'SOC2 Evidence',
  }[t]
}

// ── SOC2 Evidence (A8b.7) ──────────────────────────────────────────────────

function SOC2EvidencePanel() {
  const [bundles, setBundles] = useState<SOC2EvidenceBundle[]>([])
  const [loading, setLoading] = useState(false)
  const [stepUp, setStepUp] = useState<null | (() => Promise<void>)>(null)
  const [pwd, setPwd] = useState('')

  const load = async () => {
    try {
      setBundles(await zeroTrustService.listEvidenceBundles())
    } catch (eRaw) {
      const e = eRaw as ApiErrorLike
      toast.error(e?.response?.data?.detail || 'Failed to load bundles')
    }
  }

  useEffect(() => {
    load()
  }, [])

  const generate = async () => {
    setLoading(true)
    try {
      const { blob, sha256 } = await zeroTrustService.downloadEvidencePackage()
      downloadBlob(blob, `soc2-evidence-${sha256.slice(0, 12)}.zip`)
      toast.success('Evidence package downloaded')
      await load()
    } catch (eRaw) {
      const e = eRaw as ApiErrorLike
      // A8b.5 — server signals step-up via 401 + X-Require-Step-Up.
      const requiresStepUp = e?.response?.headers?.['x-require-step-up'] === 'true'
      if (requiresStepUp) {
        setStepUp(() => generate)
      } else {
        toast.error(e?.response?.data?.detail || 'Failed to generate package')
      }
    } finally {
      setLoading(false)
    }
  }

  const confirmStepUp = async () => {
    try {
      await zeroTrustService.confirmStepUp('password', pwd)
      toast.success('Step-up confirmed')
      setPwd('')
      const retry = stepUp
      setStepUp(null)
      if (retry) await retry()
    } catch (eRaw) {
      const e = eRaw as ApiErrorLike
      toast.error(e?.response?.data?.detail || 'Invalid password')
    }
  }

  return (
    <div className="space-y-4">
      <div className="bg-[#F8FAFC] border border-[#E4ECF1] rounded p-4 space-y-2">
        <h3 className="text-base font-semibold text-[#102a43]">
          SOC2 evidence package
        </h3>
        <p className="text-sm text-[#556B7C]">
          Generates a ZIP containing the auth policy snapshot, SSO + SCIM
          configuration (redacted), MFA coverage, 90 days of audit-log chain
          + verification proof, encryption-backend attestation, and the
          PII-encryption registry. Persisted with SHA-256 for audit chain-of-custody.
        </p>
        <button
          onClick={generate}
          disabled={loading}
          className="px-3 py-2 text-sm font-medium bg-[#0967D2] text-white rounded disabled:opacity-50"
        >
          {loading ? 'Generating…' : 'Generate + download evidence'}
        </button>
      </div>

      {stepUp && (
        <div className="bg-amber-50 border border-amber-200 rounded p-4 space-y-2">
          <p className="text-sm text-amber-900">
            This is a privileged action. Confirm your password to continue.
          </p>
          <div className="flex gap-2">
            <input
              type="password"
              value={pwd}
              onChange={e => setPwd(e.target.value)}
              placeholder="Password"
              className="flex-1 px-2 py-1 border border-amber-300 rounded text-sm"
            />
            <button
              onClick={confirmStepUp}
              className="px-3 py-1 text-sm bg-amber-600 text-white rounded"
            >
              Confirm
            </button>
            <button
              onClick={() => { setStepUp(null); setPwd('') }}
              className="px-3 py-1 text-sm border border-amber-300 rounded"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div>
        <h4 className="text-sm font-semibold text-[#102a43] mb-2">
          Previously generated bundles
        </h4>
        {bundles.length === 0 ? (
          <p className="text-sm text-[#556B7C]">No bundles generated yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[#556B7C] border-b border-[#E4ECF1]">
                <th className="py-1">When</th>
                <th>SHA-256 (head)</th>
                <th>Size</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {bundles.map(b => (
                <tr key={b.id} className="border-b border-[#E4ECF1]">
                  <td className="py-1">{b.generated_at?.replace('T', ' ').slice(0, 19)}</td>
                  <td className="font-mono text-xs">{b.sha256.slice(0, 16)}…</td>
                  <td>{(b.size_bytes / 1024).toFixed(1)} KB</td>
                  <td>{b.notes || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

// ── Auth Policy ────────────────────────────────────────────────────────────

function AuthPolicyPanel() {
  const [p, setP] = useState<AuthPolicy | null>(null)
  const [saving, setSaving] = useState(false)
  useEffect(() => {
    enterpriseTrustService.getAuthPolicy().then(setP).catch(() => toast.error('Failed to load policy'))
  }, [])
  if (!p) return <p className="text-sm text-[#556B7C]">Loading…</p>
  const upd = (patch: Partial<AuthPolicy>) => setP({ ...p, ...patch })
  return (
    <div className="space-y-3 max-w-2xl">
      <Field label="Session timeout (minutes)">
        <input type="number" min={5} max={10080} value={p.session_timeout_minutes}
          onChange={e => upd({ session_timeout_minutes: parseInt(e.target.value) || 480 })}
          className="border border-[#E4ECF1] rounded px-2 py-1 w-32" />
      </Field>
      <Field label="MFA required for all users">
        <input type="checkbox" checked={p.mfa_required}
          onChange={e => upd({ mfa_required: e.target.checked })} />
      </Field>
      <Field label="Password min length">
        <input type="number" min={8} max={128} value={p.password_min_length}
          onChange={e => upd({ password_min_length: parseInt(e.target.value) || 12 })}
          className="border border-[#E4ECF1] rounded px-2 py-1 w-32" />
      </Field>
      <div className="grid grid-cols-2 gap-2 text-sm">
        {(['upper', 'lower', 'digit', 'symbol'] as const).map(k => (
          <label key={k} className="flex items-center gap-2">
            <input type="checkbox" checked={(p as any)[`password_require_${k}`]}
              onChange={e => upd({ [`password_require_${k}`]: e.target.checked } as any)} />
            Require {k}
          </label>
        ))}
      </div>
      <Field label="Password rotation (days, blank = never)">
        <input type="number" min={1} max={3650} value={p.password_rotation_days ?? ''}
          onChange={e => upd({ password_rotation_days: e.target.value ? parseInt(e.target.value) : null })}
          className="border border-[#E4ECF1] rounded px-2 py-1 w-32" />
      </Field>
      <Field label="IP allowlist (CIDR, one per line; blank = allow all)">
        <textarea value={(p.ip_allowlist || []).join('\n')}
          onChange={e => upd({
            ip_allowlist: e.target.value.split('\n').map(s => s.trim()).filter(Boolean) || null,
          })}
          rows={4} className="border border-[#E4ECF1] rounded px-2 py-1 w-full font-mono text-xs" />
      </Field>
      <button disabled={saving} onClick={async () => {
        setSaving(true)
        try { setP(await enterpriseTrustService.saveAuthPolicy(p)); toast.success('Saved') }
        catch { toast.error('Save failed') }
        finally { setSaving(false) }
      }}
      className="px-4 py-2 bg-[#0967D2] text-white rounded text-sm font-medium">
        {saving ? 'Saving…' : 'Save policy'}
      </button>
    </div>
  )
}

// ── SSO ────────────────────────────────────────────────────────────────────

function SSOPanel() {
  const [rows, setRows] = useState<SSOConfig[]>([])
  const [editing, setEditing] = useState<Partial<SSOConfig> | null>(null)
  const reload = () => enterpriseTrustService.listSSO().then(setRows)
  useEffect(() => { reload() }, [])
  return (
    <div className="space-y-3">
      <button onClick={() => setEditing({ protocol: 'oidc', name: '', just_in_time_provisioning: true })}
        className="px-3 py-1.5 bg-[#0967D2] text-white rounded text-sm">
        Add SSO config
      </button>
      <ul className="divide-y divide-[#E4ECF1] border border-[#E4ECF1] rounded">
        {rows.length === 0 && <li className="p-3 text-sm text-[#556B7C]">No SSO configs.</li>}
        {rows.map(r => (
          <li key={r.id} className="p-3 flex justify-between items-center text-sm">
            <div>
              <strong>{r.name}</strong> <span className="text-[#556B7C]">({r.protocol.toUpperCase()})</span>
              {!r.is_active && <span className="ml-2 text-xs text-red-600">inactive</span>}
            </div>
            <div className="flex gap-2">
              <button onClick={() => setEditing(r)} className="text-[#0967D2]">Edit</button>
              <button onClick={async () => {
                if (!confirm('Delete SSO config?')) return
                await enterpriseTrustService.deleteSSO(r.id); reload()
              }} className="text-red-600">Delete</button>
            </div>
          </li>
        ))}
      </ul>
      {editing && <SSOEditor draft={editing} onClose={() => { setEditing(null); reload() }} />}
    </div>
  )
}

function SSOEditor({ draft, onClose }: { draft: Partial<SSOConfig>, onClose: () => void }) {
  const [cfg, setCfg] = useState<Partial<SSOConfig>>(draft)
  const isOidc = cfg.protocol === 'oidc'
  return (
    <div className="border border-[#0967D2] rounded p-4 bg-[#F8FAFC] space-y-2 max-w-2xl">
      <h3 className="font-semibold">{cfg.id ? 'Edit' : 'New'} {cfg.protocol?.toUpperCase()} config</h3>
      <Field label="Name">
        <input value={cfg.name || ''} onChange={e => setCfg({ ...cfg, name: e.target.value })}
          className="border rounded px-2 py-1 w-full" />
      </Field>
      <Field label="Protocol">
        <select value={cfg.protocol} disabled={!!cfg.id}
          onChange={e => setCfg({ ...cfg, protocol: e.target.value as typeof cfg.protocol })}
          className="border rounded px-2 py-1">
          <option value="oidc">OIDC</option>
          <option value="saml">SAML</option>
        </select>
      </Field>
      {isOidc ? (
        <>
          <Field label="Issuer (e.g. https://acme.okta.com)">
            <input value={cfg.oidc_issuer || ''} onChange={e => setCfg({ ...cfg, oidc_issuer: e.target.value })}
              className="border rounded px-2 py-1 w-full" />
          </Field>
          <Field label="Client ID">
            <input value={cfg.oidc_client_id || ''} onChange={e => setCfg({ ...cfg, oidc_client_id: e.target.value })}
              className="border rounded px-2 py-1 w-full" />
          </Field>
          <Field label={`Client secret ${cfg.has_client_secret ? '(leave blank to keep existing)' : ''}`}>
            <input type="password" value={cfg.oidc_client_secret || ''}
              onChange={e => setCfg({ ...cfg, oidc_client_secret: e.target.value })}
              className="border rounded px-2 py-1 w-full" />
          </Field>
          <Field label="Redirect URI">
            <input value={cfg.oidc_redirect_uri || ''} onChange={e => setCfg({ ...cfg, oidc_redirect_uri: e.target.value })}
              placeholder={`${window.location.origin}/sso/callback`}
              className="border rounded px-2 py-1 w-full" />
            <span className="mt-1 block text-xs text-muted-foreground">
              Leave blank to use <code>{window.location.origin}/sso/callback</code>. Register that address with your
              identity provider. People then sign in from the login page with “Sign in with SSO” and their work email.
            </span>
          </Field>
        </>
      ) : (
        <>
          <Field label="IdP Entity ID"><input value={cfg.saml_entity_id || ''}
            onChange={e => setCfg({ ...cfg, saml_entity_id: e.target.value })}
            className="border rounded px-2 py-1 w-full" /></Field>
          <Field label="IdP SSO URL"><input value={cfg.saml_sso_url || ''}
            onChange={e => setCfg({ ...cfg, saml_sso_url: e.target.value })}
            className="border rounded px-2 py-1 w-full" /></Field>
          <Field label="IdP X.509 Cert (PEM)"><textarea value={cfg.saml_x509_cert || ''}
            onChange={e => setCfg({ ...cfg, saml_x509_cert: e.target.value })}
            rows={4} className="border rounded px-2 py-1 w-full font-mono text-xs" /></Field>
          <Field label="SP Entity ID"><input value={cfg.saml_sp_entity_id || ''}
            onChange={e => setCfg({ ...cfg, saml_sp_entity_id: e.target.value })}
            className="border rounded px-2 py-1 w-full" /></Field>
          <Field label="SP ACS URL"><input value={cfg.saml_sp_acs_url || ''}
            onChange={e => setCfg({ ...cfg, saml_sp_acs_url: e.target.value })}
            className="border rounded px-2 py-1 w-full" /></Field>
        </>
      )}
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={cfg.just_in_time_provisioning ?? true}
          onChange={e => setCfg({ ...cfg, just_in_time_provisioning: e.target.checked })} />
        Just-in-time user provisioning
      </label>
      <div className="flex gap-2">
        <button onClick={async () => {
          try {
            if (cfg.id) await enterpriseTrustService.updateSSO(cfg.id, cfg)
            else await enterpriseTrustService.createSSO(cfg)
            toast.success('Saved'); onClose()
          } catch (eRaw) {
            const e = eRaw as ApiErrorLike
            toast.error(e.response?.data?.detail || 'Save failed')
          }
        }} className="px-3 py-1.5 bg-[#0967D2] text-white rounded text-sm">Save</button>
        <button onClick={onClose} className="px-3 py-1.5 border border-[#E4ECF1] rounded text-sm">Cancel</button>
      </div>
    </div>
  )
}

// ── SCIM ───────────────────────────────────────────────────────────────────

function SCIMPanel() {
  const [tokens, setTokens] = useState<SCIMTokenInfo[]>([])
  const [name, setName] = useState('')
  const [newSecret, setNewSecret] = useState<string | null>(null)
  const reload = () => enterpriseTrustService.listSCIMTokens().then(setTokens)
  useEffect(() => { reload() }, [])
  return (
    <div className="space-y-3 max-w-2xl">
      <p className="text-sm text-[#556B7C]">
        Generate a bearer token for your IdP's SCIM client. The token is shown only once.
      </p>
      <div className="flex gap-2">
        <input value={name} onChange={e => setName(e.target.value)} placeholder="Token name (e.g. Okta)"
          className="border border-[#E4ECF1] rounded px-2 py-1 flex-1" />
        <button onClick={async () => {
          if (!name.trim()) { toast.error('Name required'); return }
          const r = await enterpriseTrustService.createSCIMToken(name.trim())
          setNewSecret(r.token); setName(''); reload()
        }} className="px-3 py-1.5 bg-[#0967D2] text-white rounded text-sm">Create</button>
      </div>
      {newSecret && (
        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded">
          <p className="text-xs font-semibold">Copy this token — it will not be shown again:</p>
          <code className="text-xs break-all">{newSecret}</code>
          <button onClick={() => setNewSecret(null)} className="ml-2 text-xs text-[#556B7C]">dismiss</button>
        </div>
      )}
      <ul className="divide-y divide-[#E4ECF1] border border-[#E4ECF1] rounded">
        {tokens.length === 0 && <li className="p-3 text-sm text-[#556B7C]">No tokens.</li>}
        {tokens.map(t => (
          <li key={t.id} className="p-3 flex justify-between items-center text-sm">
            <div>
              <strong>{t.name}</strong>
              <span className="text-xs text-[#556B7C] ml-2">
                {t.is_active ? `last used: ${t.last_used_at || 'never'}` : 'revoked'}
              </span>
            </div>
            {t.is_active && (
              <button onClick={async () => {
                if (!confirm('Revoke token?')) return
                await enterpriseTrustService.revokeSCIMToken(t.id); reload()
              }} className="text-red-600">Revoke</button>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

// ── Audit ──────────────────────────────────────────────────────────────────

function AuditPanel() {
  const [rows, setRows] = useState<AuditEntry[]>([])
  const [count, setCount] = useState(0)
  const [verify, setVerify] = useState<{ ok: boolean; entries: number; mismatch_at: number | null } | null>(null)
  useEffect(() => {
    enterpriseTrustService.listAudit({ limit: 50 }).then(r => { setRows(r.items); setCount(r.count) })
  }, [])
  return (
    <div className="space-y-3">
      <div className="flex gap-2 flex-wrap">
        <button onClick={async () => {
          const r = await enterpriseTrustService.verifyAudit(); setVerify(r)
        }} className="px-3 py-1.5 border border-[#E4ECF1] rounded text-sm">
          Verify chain
        </button>
        <button onClick={async () => {
          const blob = await enterpriseTrustService.exportAudit('json')
          downloadBlob(blob, `audit-${Date.now()}.json`)
        }} className="px-3 py-1.5 border border-[#E4ECF1] rounded text-sm">
          Export JSON
        </button>
        <button onClick={async () => {
          const blob = await enterpriseTrustService.exportAudit('csv')
          downloadBlob(blob, `audit-${Date.now()}.csv`)
        }} className="px-3 py-1.5 border border-[#E4ECF1] rounded text-sm">
          Export CSV
        </button>
        {verify && (
          <span className={`text-sm self-center ${verify.ok ? 'text-green-700' : 'text-red-700'}`}>
            {verify.ok ? `Chain OK (${verify.entries} entries)` : `Chain BROKEN at id=${verify.mismatch_at}`}
          </span>
        )}
      </div>
      <p className="text-xs text-[#556B7C]">{count} entries total</p>
      <div className="border border-[#E4ECF1] rounded overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-[#F8FAFC]">
            <tr>
              <th className="p-2 text-left">ID</th><th className="p-2 text-left">When</th>
              <th className="p-2 text-left">User</th><th className="p-2 text-left">Entity</th>
              <th className="p-2 text-left">Action</th>
              <th className="p-2 text-left">Description</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} className="border-t border-[#E4ECF1]">
                <td className="p-2 font-mono">{r.id}</td>
                <td className="p-2">{r.created_at?.slice(0, 19)}</td>
                <td className="p-2">{r.user_id ?? '-'}</td>
                <td className="p-2">{r.entity_type}{r.entity_id ? `:${r.entity_id}` : ''}</td>
                <td className="p-2">{r.action}</td>
                <td className="p-2">{r.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ── Tenant Data ────────────────────────────────────────────────────────────

function TenantDataPanel() {
  const [status, setStatus] = useState<DeletionStatus | null>(null)
  const [reason, setReason] = useState('')
  const [grace, setGrace] = useState(30)
  const reload = () => enterpriseTrustService.tenantDeletionStatus().then(setStatus)
  useEffect(() => { reload() }, [])
  return (
    <div className="space-y-4 max-w-2xl">
      <section>
        <h3 className="font-semibold">Export tenant data</h3>
        <p className="text-sm text-[#556B7C]">
          Full JSON archive of every row owned by this tenant — useful for GDPR portability.
        </p>
        <button onClick={async () => {
          const blob = await enterpriseTrustService.exportTenant()
          downloadBlob(blob, `tenant-${Date.now()}.json`)
        }} className="mt-2 px-3 py-1.5 bg-[#0967D2] text-white rounded text-sm">
          Download archive
        </button>
      </section>
      <section>
        <h3 className="font-semibold text-red-700">Delete tenant</h3>
        <p className="text-sm text-[#556B7C]">
          Permanently deletes all data after the grace period. You can cancel any time before then.
        </p>
        {status && status.status === 'pending' ? (
          <div className="border border-red-200 bg-red-50 rounded p-3 mt-2">
            <p className="text-sm">
              <strong>Deletion scheduled</strong> for {status.scheduled_for?.slice(0, 19)}
            </p>
            <button onClick={async () => {
              await enterpriseTrustService.cancelTenantDeletion(); reload(); toast.success('Cancelled')
            }} className="mt-2 px-3 py-1.5 bg-[#0967D2] text-white rounded text-sm">
              Cancel deletion
            </button>
          </div>
        ) : (
          <div className="mt-2 space-y-2">
            <Field label="Reason (optional)">
              <input value={reason} onChange={e => setReason(e.target.value)}
                className="border rounded px-2 py-1 w-full" />
            </Field>
            <Field label="Grace period (days)">
              <input type="number" min={1} max={365} value={grace}
                onChange={e => setGrace(parseInt(e.target.value) || 30)}
                className="border rounded px-2 py-1 w-32" />
            </Field>
            <button onClick={async () => {
              if (!confirm('This will delete EVERY row owned by this tenant after the grace period. Continue?'))
                return
              await enterpriseTrustService.requestTenantDeletion({
                reason, grace_period_days: grace
              })
              reload(); toast.success('Deletion scheduled')
            }} className="px-3 py-1.5 bg-red-600 text-white rounded text-sm">
              Schedule deletion
            </button>
          </div>
        )}
      </section>
    </div>
  )
}

// ── shared ────────────────────────────────────────────────────────────────

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-sm">
      <span className="block text-xs font-medium text-[#556B7C] mb-1">{label}</span>
      {children}
    </label>
  )
}
